
import {
  ChangeDetectorRef,
  Component, ElementRef, forwardRef, HostListener, Input, OnDestroy, OnInit
} from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { map, takeUntil } from 'rxjs/operators';

import { Tag, UserRole, UserWithTurfAssignments } from 'generated/graphql';

import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { isNilOrEmpty } from 'helpers';

export interface AccountTurfInputModel {
  restrictionEnabled: boolean;
  tagIds: string[];
}

/**
 * The model that the options must conform to
 */
export interface AccountTurfOptionsModel {
  tagGroupName: string;
  tagCategoryName: string;
  tags: Tag[];
}

export function gqlTurfAssignmentToInputMapper(input: UserWithTurfAssignments.TurfAssignments): AccountTurfInputModel {
  const result: AccountTurfInputModel = {
    restrictionEnabled: input.restrictionEnabled,
    tagIds: input.turfTags.map(t => t.tag.id)
  };

  return result;
}

@Component({
  selector: 'app-account-turf-input',
  templateUrl: './account-turf-input.component.html',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AccountTurfInputComponent), multi: true }
  ]
})
export class AccountTurfInputComponent extends WithDisposableSubject()
  implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() role: UserRole;
  @Input() campaignId: string;

  expanded = false;
  expandedOptionGroups: AccountTurfOptionsModel[] = [];

  tagIds = [];

  // Limit the visibility to 50 at a time
  visibleTagCount = 0;
  visibleTagIncrement = 50;
  visibleTagIds = [];

  disabled = false;
  filterTextControl = new UntypedFormControl();
  restrictionEnabledControl = new UntypedFormControl();

  get selectedResponses(): { id: string }[] {
    return this.tagIds.map(id => ({ id }));
  }
  /**
   *
   */
  constructor(
    private eRef: ElementRef,
    private _cd: ChangeDetectorRef
  ) {
    super();
  }

  get value(): AccountTurfInputModel {
    return {
      tagIds: this.tagIds,
      restrictionEnabled: this.restrictionEnabledControl.value
    };
  }

  // Handles clicks outside of the picker
  // and close it if it is open
  @HostListener('document:click', ['$event'])
  @HostListener('document:touchstart', ['$event'])
  handleOutsideClick(event) {
    if (this.expanded && !this.eRef.nativeElement.contains(event.target) && !event.target.hasAttribute('data-prevent-collapse')) {
      this.expanded = false;
    }
  }

  removeAllResponses() {
    const confirmed = confirm('Are you sure you want to remove all responses from the assignment?');
    if (!confirmed) {
      return;
    }
    this.onSelectedResponsesChanged([]);
  }

  onSelectedResponsesChanged(tags: any[]) {
    this.tagIds = tags.map(t => t.id);
    this.visibleTagIds = this.tagIds.slice(0, this.visibleTagCount);
    this.propagateChange(this.value);
  }

  unselectTag(tagId) {
    this.tagIds = this.tagIds.filter(id => id !== tagId);
    this.visibleTagIds = this.visibleTagIds.filter(id => id !== tagId);
    this.propagateChange(this.value);
  }

  // ** Lifecyle hooks **
  ngOnInit(): void {
    this.restrictionEnabledControl.valueChanges.pipe(
      takeUntil(this.disposer)
    ).subscribe(() => this.propagateChange(this.value));
  }

  setVisibleTags() {
    this.visibleTagCount += this.visibleTagIncrement;
    this.visibleTagIds = this.tagIds.slice(0, this.visibleTagCount);
    this._cd.markForCheck();
  }

  // ControlValueAccessor implementation
  propagateChange: any = () => { };

  writeValue(value: AccountTurfInputModel): void {
    if (isNilOrEmpty(value)) {
      this.tagIds = [];
      this.restrictionEnabledControl.reset();
      this.setVisibleTags();
    } else {
      this.tagIds = value.tagIds || [];
      this.restrictionEnabledControl.setValue(value.restrictionEnabled || false);
      this.setVisibleTags();
    }

    this.propagateChange(value);
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.registerOnTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.restrictionEnabledControl.disable();
    } else {
      this.restrictionEnabledControl.enable();
    }
  }
}
